import React, { useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Paper, Stack, Tab, Tabs, Typography,
} from '@mui/material';
import {
  AddRounded as AddIcon,
  CompareArrowsRounded as PairIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { useBankAccounts } from './hooks/useBankAccounts';
import { useCustomers } from './hooks/useCustomers';
import LoanPairSetupCard from './components/dialogs/LoanPairSetupCard';
import LoanPairSettingsCard from './components/dialogs/LoanPairSettingsCard';

// หน้า "ตั้งค่าคู่ยืม-คืน" — แยกออกมาจากหน้า "ยืม-คืน" หลัก (LoanPairs.js) ตามที่ผู้ใช้ขอ
// เพราะส่วนกำหนด/แก้ไขบัญชีของคู่ยืม-คืนใช้ไม่บ่อย (ส่วนใหญ่ตั้งครั้งเดียวแล้วใช้แค่ดู
// ข้อมูลที่หน้า /accounting/loan-pairs) — มี 2 แท็บ: "คู่ยืม-คืน" (สร้าง/แก้ไข customer ฝั่งยืม-คืน)
// และ "ตั้งค่า" (ชื่อรายการเจ้าหนี้ + customer ของหน้าบัญชีบริษัท) — มีคู่เดียวเท่านั้น: ถ้าฐานข้อมูล
// มีหลายคู่อยู่แล้ว โชว์เฉพาะคู่แรกสุด (ตรงกับที่หน้า /accounting/loan-pairs ใช้) ไม่ลบอันอื่น
export function LoanPairSetup({ title, subtitle }) {
  const { bankAccounts, loading, error, reload } = useBankAccounts();
  const { customerRows, findCustomer, customerName } = useCustomers();
  const [creatingNew, setCreatingNew] = useState(false);
  const [tab, setTab] = useState(0);
  const pair = bankAccounts.find((account) => account.mode === 'BALANCE');

  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto', pb: 5 }}>
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          p: { xs: 2.5, md: 3.5 },
          borderRadius: 3.5,
          color: '#fff',
          background: 'linear-gradient(125deg, #172554 0%, #1d4ed8 62%, #0ea5e9 100%)',
          '&::after': {
            content: '""',
            position: 'absolute',
            width: 280,
            height: 280,
            borderRadius: '50%',
            right: -70,
            top: -150,
            bgcolor: 'rgba(255,255,255,.09)',
          },
        }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} alignItems={{ md: 'center' }} justifyContent="space-between">
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <PairIcon fontSize="small" />
              <Typography variant="overline" sx={{ fontWeight: 700, letterSpacing: 1.2, opacity: 0.85 }}>LOAN & REPAYMENT</Typography>
            </Stack>
            <Typography variant="h4" sx={{ fontWeight: 800, fontSize: { xs: '1.55rem', md: '2rem' } }}>
              {title || 'ตั้งค่าคู่ยืม-คืน'}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1, maxWidth: 650, opacity: 0.82 }}>
              {subtitle || 'เชื่อมโยง Customer ฝั่งยืมและฝั่งคืน (คู่เดียว) และตั้งค่าชื่อรายการเจ้าหนี้ / Customer ของหน้าบัญชีบริษัท'}
            </Typography>
          </Box>
          {!loading && !pair && (
            <Button
              variant="contained"
              size="large"
              startIcon={<AddIcon />}
              onClick={() => { setTab(0); setCreatingNew(true); }}
              disabled={creatingNew}
              sx={{
                position: 'relative',
                zIndex: 1,
                alignSelf: { xs: 'stretch', md: 'center' },
                bgcolor: '#fff',
                color: '#1d4ed8',
                fontWeight: 750,
                px: 2.5,
                '&:hover': { bgcolor: '#eff6ff' },
              }}
            >
              สร้างคู่ยืม-คืน
            </Button>
          )}
        </Stack>
      </Paper>

      <Tabs value={tab} onChange={(e, value) => setTab(value)} sx={{ mt: 2.5, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="คู่ยืม-คืน" />
        <Tab label="ตั้งค่า" />
      </Tabs>

      {error && <Alert severity="error" sx={{ mt: 2.5, borderRadius: 2.5 }}>{error}</Alert>}

      {tab === 0 && (
        <>
          {creatingNew && (
            <Box sx={{ mt: 2.5 }}>
              <Alert icon={<InfoIcon />} severity="info" sx={{ mb: 1.5, borderRadius: 2.5 }}>
                เลือก Customer ทั้งสองฝั่ง ระบบจะดึงข้อมูลบัญชีธนาคารจากข้อมูล Customer ให้อัตโนมัติ
              </Alert>
              <LoanPairSetupCard
                pair={null}
                customerRows={customerRows}
                findCustomer={findCustomer}
                customerName={customerName}
                onSaved={() => { setCreatingNew(false); reload(); }}
                onCancelNew={() => setCreatingNew(false)}
              />
            </Box>
          )}

          {loading ? (
            <Paper variant="outlined" sx={{ mt: 2.5, minHeight: 240, display: 'grid', placeItems: 'center', borderRadius: 3 }}>
              <Stack spacing={1.5} alignItems="center"><CircularProgress size={32} /><Typography variant="body2" color="text.secondary">กำลังโหลดข้อมูล...</Typography></Stack>
            </Paper>
          ) : !pair && !creatingNew ? (
            <Paper variant="outlined" sx={{ mt: 2.5, p: { xs: 3, md: 5 }, borderRadius: 3, borderStyle: 'dashed', textAlign: 'center' }}>
              <Typography variant="h6" sx={{ fontWeight: 750 }}>ยังไม่มีคู่ยืม-คืน</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>สร้างคู่ยืม-คืนเพื่อเริ่มใช้งาน (มีได้คู่เดียว)</Typography>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreatingNew(true)}>สร้างคู่ยืม-คืน</Button>
            </Paper>
          ) : pair ? (
            <Box sx={{ mt: 2.5 }}>
              <LoanPairSetupCard
                pair={pair}
                customerRows={customerRows}
                findCustomer={findCustomer}
                customerName={customerName}
                onSaved={reload}
              />
            </Box>
          ) : null}
        </>
      )}

      {tab === 1 && !loading && (
        <Box sx={{ mt: 2.5 }}>
          <LoanPairSettingsCard
            pair={pair}
            customerRows={customerRows}
            findCustomer={findCustomer}
            customerName={customerName}
            onSaved={reload}
          />
        </Box>
      )}
    </Box>
  );
}

export default LoanPairSetup;
